import React, { useState } from 'react';
import { Droppable } from '@hello-pangea/dnd';
import Card from './Card';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pencil, Check, Plus, X, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Task {
  id: string;
  content: string;
  labels?: { type: 'urgent' | 'critical' | 'normal' | 'low' }[];
}

interface Column {
  id: string;
  title: string;
  tasks: Task[];
}

interface Props {
  column: Column;
}

const Column = ({ column }: Props) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(column.title);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [newTaskContent, setNewTaskContent] = useState('');
  const [selectedLabel, setSelectedLabel] = useState<'urgent' | 'critical' | 'normal' | 'low'>('normal');
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);

  const handleRename = async () => {
    if (!title.trim() || title === column.title) {
      setIsEditing(false);
      setTitle(column.title);
      return;
    }

    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('columns')
        .update({ 
          title: title.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', column.id);

      if (error) throw error;

      toast({
        title: t("common.columns.renamed"),
        description: t("common.columns.renamedDescription"),
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error renaming column:', error);
      toast({
        title: t("common.error"),
        description: t("common.columns.errorRenaming"),
        variant: "destructive",
      });
      setTitle(column.title);
      setIsEditing(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleRename();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setTitle(column.title);
    }
  };

  const handleAddTask = async () => {
    if (!newTaskContent.trim()) return;

    setIsUpdating(true);
    try {
      // Insert the task
      const { data: taskData, error: taskError } = await supabase
        .from('tasks')
        .insert({
          content: newTaskContent.trim(),
          column_id: column.id,
          position: column.tasks.length,
          created_by: (await supabase.auth.getUser()).data.user?.id,
          start_date: startDate?.toISOString(),
          due_date: dueDate?.toISOString()
        })
        .select()
        .single();

      if (taskError) throw taskError;

      // Insert the label
      if (taskData) {
        const { error: labelError } = await supabase
          .from('task_labels')
          .insert({
            task_id: taskData.id,
            type: selectedLabel
          });

        if (labelError) throw labelError;
      }

      toast({
        title: t("common.tasks.added"),
        description: t("common.tasks.addedDescription"),
      });
      setIsAddingTask(false);
      setNewTaskContent('');
      setSelectedLabel('normal');
      setStartDate(undefined);
      setDueDate(undefined);
    } catch (error) {
      console.error('Error adding task:', error);
      toast({
        title: t("common.error"),
        description: t("common.tasks.errorAdding"),
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="bg-slate-100 dark:bg-slate-900 rounded-xl p-4 min-w-[320px] w-[400px] flex-shrink-0 shadow-lg border border-slate-200 dark:border-slate-700 flex flex-col h-fit max-h-[calc(100vh-10rem)]">
      <div className="flex items-center justify-between mb-4 px-2">
        {isEditing ? (
          <div className="flex items-center gap-2 w-full">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-8"
              onKeyDown={handleKeyDown}
              autoFocus
              disabled={isUpdating}
            />
            <Button
              size="icon"
              variant="ghost"
              onClick={handleRename}
              className="h-8 w-8"
              disabled={isUpdating}
            >
              <Check className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <>
            <h2 className="font-semibold text-slate-700 dark:text-slate-300">
              {column.title}
            </h2>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setIsEditing(true)}
              className="h-8 w-8"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>

      <Droppable droppableId={column.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex-1 overflow-y-auto min-h-[100px] transition-colors duration-200 rounded-lg p-2 
              ${snapshot.isDraggingOver ? 'bg-slate-200/50 dark:bg-slate-800/50' : ''}`}
            style={{
              minHeight: column.tasks.length === 0 ? '150px' : 'auto',
              height: column.tasks.length > 0 ? 'auto' : '150px'
            }}
          >
            {column.tasks.map((task, index) => (
              <Card key={task.id} task={task} index={index} />
            ))}
            {provided.placeholder}

            {isAddingTask ? (
              <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 space-y-4">
                <Input
                  placeholder={t("common.tasks.enterContent")}
                  value={newTaskContent}
                  onChange={(e) => setNewTaskContent(e.target.value)}
                  disabled={isUpdating}
                  className="w-full"
                />
                
                <Select
                  value={selectedLabel}
                  onValueChange={(value: 'urgent' | 'critical' | 'normal' | 'low') => setSelectedLabel(value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>

                <div className="flex flex-col space-y-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <Calendar className="mr-2 h-4 w-4 text-primary" />
                        {startDate ? format(startDate, 'PP') : t('common.tasks.startDate')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent 
                      className="w-auto p-0" 
                      align="start" 
                      side="right"
                      sideOffset={5}
                    >
                      <CalendarComponent
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <Calendar className="mr-2 h-4 w-4 text-accent" />
                        {dueDate ? format(dueDate, 'PP') : t('common.tasks.dueDate')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent 
                      className="w-auto p-0" 
                      align="start" 
                      side="right"
                      sideOffset={5}
                    >
                      <CalendarComponent
                        mode="single"
                        selected={dueDate}
                        onSelect={setDueDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setIsAddingTask(false);
                      setStartDate(undefined);
                      setDueDate(undefined);
                    }}
                    disabled={isUpdating}
                    className="w-10"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAddTask}
                    disabled={isUpdating}
                    className="flex-1"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t("common.tasks.add")}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="ghost"
                className="w-full mt-2"
                onClick={() => setIsAddingTask(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t("common.tasks.add")}
              </Button>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
};

export default Column;